/**
 * Documents API. All document/search/checkin-related HTTP calls go through here.
 * Use these methods inside createAsyncThunk in slices.
 */
import { getApiClient, getIfsApiClient } from "./client";
import { ENDPOINTS } from "../configs/endpoints";
import AuthTeams from "../../auth/AuthTeams";
import CustomerConfig from "../../azure/CustomerConfig";
import { logger } from "../../utils/logger";
import {
  getWorkingDocumentDisplayDocIdentifiers,
  sanitizeFileName,
} from "../../functions/helperMethod";
import {
  ERROR_TYPE,
  ERROR_DETAILS,
  SERVER_RESPONSE_ERROR,
  UNKOWN_ERROR,
  FAILED_GENERATE_DIRECT_LINK,
  SOME_DOCUMENT_ERROR,
  NO_CONTENT_FOUND,
  GRAPH_SERVICE_ERROR,
  IFS_SERVER_ERROR,
} from "../constants/messageTypes";

/**
 * Get supported language codes (used by the Templates panel).
 * Backend route: api/DocumentManagement/get-language-code
 * @returns {Promise<{ data: any, status: number }>}
 */
export async function getLanguageCode() {
  logger.info("getLanguageCode", null, "API");
  try {
    const client = getIfsApiClient();
    const res = await client.get(
      `api/DocumentManagement/get-language-code`
    );
    return { data: res.data, status: res.status };
  } catch (err) {
    logger.error("getLanguageCode failed", err, "API");
    throw err;
  }
}

/**
 * Get supported IFS file types and extensions (used by the OneDrive Documents panel).
 * Backend route: api/DocumentManagement/get-fileTypeInfo
 * @returns {Promise<{ data: any, status: number }>}
 */
export async function getFileTypeInfo() {
  logger.info("getFileTypeInfo", null, "API");
  try {
    const client = getIfsApiClient();
    const res = await client.get("api/DocumentManagement/get-fileTypeInfo");
    const data =
      res.status === 204
        ? []
        : Array.isArray(res.data)
          ? res.data
          : [];
    return { data, status: res.status };
  } catch (err) {
    logger.error("getFileTypeInfo failed", err, "API");
    throw err;
  }
}

/**
 * Get templates by language code (used by the Templates panel).
 * Backend route: api/DocumentManagement/get-templates-by-language-code
 * @param {string} languageCode - The language code (e.g., "en", "sv", etc.)
 * @returns {Promise<{ data: any, status: number }>}
 */
export async function getTemplatesByFileTypeAndLanguageCode(languageCode = "") {
  logger.info("getTemplatesByFileTypeAndLanguageCode", null, "API");
  try {
    const client = getIfsApiClient();
    const res = await client.get(
      `api/DocumentManagement/get-templates-by-language-code?languageCode=${encodeURIComponent(languageCode)}`
    );
    const data =
      res.status === 204
        ? []
        : Array.isArray(res.data)
          ? res.data
          : [];
    return { data, status: res.status };
  } catch (err) {
    logger.error("getTemplatesByFileTypeAndLanguageCode failed", err, "API");
    throw err;
  }
}

/** Default axios timeout for template IFS download + OneDrive upload (large PPT/PDF). */
const TEMPLATE_ONEDRIVE_DOWNLOAD_TIMEOUT_MS = 10 * 60 * 1000;

/** IFS → OneDrive (checkout / download); capped at 4 minutes. */
export const UPLOAD_TO_ONEDRIVE_TIMEOUT_MS = 4 * 60 * 1000;

/** Default axios client timeout (60s) is too low for sequential bulk check-out. */
const BULK_CHECKOUT_TIMEOUT_BASE_MS = 120_000;
const BULK_CHECKOUT_TIMEOUT_PER_DOC_MS = 60_000;
const BULK_CHECKOUT_TIMEOUT_MAX_MS = 240_000;

/**
 * Axios timeout for POST check-out-bulk.
 * @param {number} documentCount
 * @returns {number}
 */
export function getBulkCheckoutTimeoutMs(documentCount) {
  const count = Math.max(1, Number(documentCount) || 1);
  return Math.min(
    BULK_CHECKOUT_TIMEOUT_MAX_MS,
    BULK_CHECKOUT_TIMEOUT_BASE_MS + count * BULK_CHECKOUT_TIMEOUT_PER_DOC_MS,
  );
}

/** Check-in existing: server downloads OneDrive bytes then uploads to IFS (large PPT can exceed 60s). */
const CHECKIN_EXISTING_TIMEOUT_MIN_MS = 120_000;
const CHECKIN_EXISTING_TIMEOUT_MAX_MS = 15 * 60 * 1000;
const CHECKIN_EXISTING_TIMEOUT_PER_MB_MS = 2_000;

/**
 * Axios timeout for POST check-in-existing (OneDrive → server → IFS).
 * When file size is unknown, use the max — working-doc check-ins are often large.
 * @param {{ fileSizeBytes?: number, viewCopyBase64Length?: number }} [options]
 */
export function getCheckinExistingTimeoutMs(options = {}) {
  const fileSizeBytes = Number(options.fileSizeBytes) || 0;
  const viewCopyBase64Length = Number(options.viewCopyBase64Length) || 0;
  if (fileSizeBytes <= 0) {
    return CHECKIN_EXISTING_TIMEOUT_MAX_MS;
  }
  const fileMb = fileSizeBytes / (1024 * 1024);
  const viewExtra = Math.ceil(viewCopyBase64Length / 1024) * 50;
  const estimated =
    CHECKIN_EXISTING_TIMEOUT_MIN_MS +
    Math.ceil(fileMb) * CHECKIN_EXISTING_TIMEOUT_PER_MB_MS +
    viewExtra;
  return Math.min(
    CHECKIN_EXISTING_TIMEOUT_MAX_MS,
    Math.max(CHECKIN_EXISTING_TIMEOUT_MIN_MS, estimated),
  );
}

/**
 * Download a template file from IFS and upload it to OneDrive (shown in Working Documents).
 * Backend route: api/DocumentManagement/download-template-to-onedrive
 */
export async function downloadTemplateToOneDrive(accessToken, params) {
  logger.info("downloadTemplateToOneDrive", null, "API");
  try {
    const client = getApiClient(accessToken);
    const {
      tenantId, teamId = "", channelId = "", oneDriveRoot,
      userId, webUser,
      docClass, docNo, docSheet, docRev, formatSize, languageCode, title,
    } = params;
    const oneDrivePart = buildUploadToOneDriveQuery(userId, tenantId ?? "", teamId ?? "", channelId ?? "", oneDriveRoot, webUser);
    const templatePart = [
      `docClass=${encodeURIComponent(docClass)}`,
      `docNo=${encodeURIComponent(docNo)}`,
      `docSheet=${encodeURIComponent(docSheet)}`,
      `docRev=${encodeURIComponent(docRev)}`,
      `formatSize=${encodeURIComponent(formatSize)}`,
      `languageCode=${encodeURIComponent(languageCode)}`,
      `title=${encodeURIComponent(title ?? docNo)}`,
    ].join("&");
    const res = await client.post(
      `api/DocumentManagement/download-template-to-onedrive?${oneDrivePart}&${templatePart}`,
      null,
      { timeout: TEMPLATE_ONEDRIVE_DOWNLOAD_TIMEOUT_MS }
    );
    return { data: res.data, status: res.status };
  } catch (err) {
    logger.error("downloadTemplateToOneDrive failed", err, "API");
    throw err;
  }
}

/**
 * Download a template from IFS. Matches Share add-in get-templates-download.
 * When uploadOneDrive is true, uploads to OneDrive and returns URLs; otherwise returns fileBase64 or a file stream.
 * Backend route: api/DocumentManagement/get-templates-download
 */
export async function getTemplatesDownload(accessToken, params) {
  logger.info("getTemplatesDownload", null, "API");
  try {
    const client = getApiClient(accessToken);
    const {
      docClass, docNo, docSheet, docRev, formatSize, languageCode, title,
      uploadOneDrive = false, userToken, downloadMode,
      userId, tenantId, teamId = "", channelId = "", oneDriveRoot,
    } = params;
    const query = [
      `docClass=${encodeURIComponent(docClass)}`,
      `docNo=${encodeURIComponent(docNo)}`,
      `docSheet=${encodeURIComponent(docSheet)}`,
      `docRev=${encodeURIComponent(docRev)}`,
      `formatSize=${encodeURIComponent(formatSize)}`,
      `languageCode=${encodeURIComponent(languageCode)}`,
      `title=${encodeURIComponent(title ?? docNo)}`,
      `uploadOneDrive=${uploadOneDrive}`,
    ];
    if (userToken) query.push(`userToken=${encodeURIComponent(userToken)}`);
    if (downloadMode) query.push(`downloadMode=${encodeURIComponent(downloadMode)}`);
    if (uploadOneDrive) {
      if (userId) query.push(`userId=${encodeURIComponent(userId)}`);
      if (tenantId) query.push(`tenantId=${encodeURIComponent(tenantId)}`);
      if (teamId) query.push(`teamId=${encodeURIComponent(teamId)}`);
      if (channelId) query.push(`channelId=${encodeURIComponent(channelId)}`);
      if (oneDriveRoot) query.push(`oneDriveRoot=${encodeURIComponent(oneDriveRoot)}`);
    }
    const res = await client.get(`api/DocumentManagement/get-templates-download?${query.join("&")}`);
    return { data: res.data, status: res.status, headers: res.headers };
  } catch (err) {
    logger.error("getTemplatesDownload failed", err, "API");
    throw err;
  }
}

/**
 * Get recent objects for check-in (MRU).
 * @returns {Promise<{ data: any, status: number }>}
 */
export async function getRecentObjects() {
  logger.info("getRecentObjects", null, "API");
  try {
    const client = getIfsApiClient();
    const webUser = new AuthTeams().getEmail();
    let ifsUser = "";
    try {
      const raw = localStorage.getItem("ifsUserDetails");
      const parsed = raw ? JSON.parse(raw) : null;
      ifsUser = parsed?.UserEmail ?? parsed?.WebUser ?? "";
    } catch {
      ifsUser = "";
    }
    const ifsUserQuery = ifsUser ? `&ifsUser=${encodeURIComponent(ifsUser)}` : "";
    const res = await client.get(
      `api/DocumentManagement/get-recentobjects?webUser=${encodeURIComponent(webUser)}${ifsUserQuery}`,
    );
    return { data: res.data, status: res.status };
  } catch (err) {
    logger.error("getRecentObjects failed", err, "API");
    throw err;
  }
}

/**
 * Merge/export documents (example for createAsyncThunk).
 * @param {object} params
 * @param {string} params.format
 * @param {any} params.docs
 * @param {string} [params.mergeSessionId]
 * @param {string} params.userToken
 * @param {boolean} [params.downloadMode]
 * @param {string} accessToken
 * @returns {Promise<any>}
 */
export async function mergeExportDocuments(
  { format, docs, mergeSessionId, userToken, downloadMode },
  accessToken,
) {
  logger.info("mergeExportDocuments", { format }, "API");
  try {
    const client = getApiClient(accessToken);
    const res = await client.post("api/merge-export-documents", {
      format,
      docs,
      mergeSessionId,
      userToken,
      downloadMode,
    });
    return res.data;
  } catch (err) {
    logger.error("mergeExportDocuments failed", err, "API");
    throw err;
  }
}

/**
 * Search documents (IFS). Calls backend api/get-search?filters=... (same as legacy flow).
 * @param {object} params - search params (searchValue, pageSize, pageIndex, tenantId, etc.)
 * @param {string} accessToken
 * @returns {Promise<{ data: any, status: number }>}
 */
export async function searchDocuments(params, accessToken) {
  logger.info("searchDocuments", null, "API");
  try {
    const auth = new AuthTeams();
    const pageIndex = params.pageIndex ?? 0;
    const pageSize = params.pageSize ?? 10;
    const skipRows = pageIndex * pageSize;

    const normalizedSearchType =
      params.searchType === "ADVANCED_SEARCH"
        ? "CONTENT"
        : params.searchType === "SEARCH_BY_CONTENT"
          ? "CONTENT"
          : "BASIC";

    const filters = {
      webUser: auth.getEmail(),
      searchText: params.searchValue ?? "",
      skipRows,
      pageSize,
      searchType: normalizedSearchType,
      releaseLevel: params.advanceSearchReleaseLevel ?? "",
      fromDate: params.advanceSearchStartDate ?? "",
      toDate: params.advanceSearchEndDate ?? "",
      anyDate: params.advanceSearchAnyDate ?? false,
      userInvolved: params.advanceSearchOnlyUserInvolved ? auth.getIfsWebUser() : "",
      isUserInvolvedOnly: params.advanceSearchOnlyUserInvolved ?? false,
      docClass: params.advanceSearchDocClass ?? "''",
      docFormat: params.advanceSearchFormat ?? "''",
      docRev: "''",
      fileStatus: params.fileStatus ?? "''",
      tenantId: params.tenantId ?? "",
      groupId: params.groupId ?? "",
      channelId: params.channelId ?? "",
      lastCheckOutMe: params.lastCheckOutMe ?? false,
      fileExtention: params.docType ?? "''",
      isIfsFileAccessible: true,
    };

    const client = getApiClient(accessToken);
    const filterString = encodeURIComponent(JSON.stringify(filters));
    const res = await client.get(
      `api/Document/get-search?filters=${filterString}`,
    );
    return { data: res.data, status: res.status };
  } catch (err) {
    logger.error("searchDocuments failed", err, "API");
    throw err;
  }
}

/**
 * Get new revision info for a selected document (latest revision, next revision, class details, view copy, etc.).
 * Calls backend api/get-newrevisioninfo?webUser=...&docClass=...&docNo=...&docSheet=...&docRev=...&requestDocRevInfo=...
 * @param {object} params - { docClass, docNo, docSheet, docRev, requestDocRevInfo }
 * @returns {Promise<{ data: any, status: number }>}
 */
export async function getNewRevisionInfo(params) {
  logger.info("getNewRevisionInfo", null, "API");
  try {
    const auth = new AuthTeams();
    const webUser = encodeURIComponent(auth.getEmail());
    const { docClass, docNo, docSheet, docRev, requestDocRevInfo } = params;
    const reqRev = requestDocRevInfo === true || requestDocRevInfo === "true";
    const q = `webUser=${webUser}&docClass=${encodeURIComponent(docClass ?? "")}&docNo=${encodeURIComponent(docNo ?? "")}&docSheet=${encodeURIComponent(docSheet ?? "")}&docRev=${encodeURIComponent(docRev ?? "")}&requestDocRevInfo=${reqRev}`;
    const client = getIfsApiClient();
    const res = await client.get(
      `api/DocumentManagement/get-newrevisioninfo?${q}`,
    );
    return { data: res.data, status: res.status };
  } catch (err) {
    logger.error("getNewRevisionInfo failed", err, "API");
    throw err;
  }
}

/**
 * Get total record count for current search filters (pagination). Calls backend api/get-search-records-count?filters=...
 * Response shape: { value: number, eTag?: string } (e.g. {"value":3308,"eTag":""}).
 * Uses the same filter shape as searchDocuments (skipRows/pageSize can be 0 for count).
 * @param {object} params - same search params as searchDocuments (searchValue, tenantId, etc.)
 * @param {string} accessToken
 * @returns {Promise<{ data: { value: number, eTag?: string }, status: number }>}
 */
export async function getSearchRecordsCount(params, accessToken) {
  logger.info("getSearchRecordsCount", null, "API");
  try {
    const auth = new AuthTeams();
    const normalizedSearchType =
      params.searchType === "ADVANCED_SEARCH"
        ? "CONTENT"
        : params.searchType === "SEARCH_BY_CONTENT"
          ? "CONTENT"
          : "BASIC";
    const filters = {
      webUser: auth.getEmail(),
      searchText: params.searchValue ?? "",
      skipRows: 0,
      pageSize: 0,
      searchType: normalizedSearchType,
      releaseLevel: params.advanceSearchReleaseLevel ?? "",
      fromDate: params.advanceSearchStartDate ?? "",
      toDate: params.advanceSearchEndDate ?? "",
      anyDate: params.advanceSearchAnyDate ?? false,
      userInvolved: params.advanceSearchOnlyUserInvolved ? auth.getIfsWebUser() : "",
      docClass: params.advanceSearchDocClass ?? "''",
      docFormat: params.advanceSearchFormat ?? "''",
      docRev: "''",
      fileStatus: params.fileStatus ?? "''",
      tenantId: params.tenantId ?? "",
      groupId: params.groupId ?? "",
      channelId: params.channelId ?? "",
      lastCheckOutMe: params.lastCheckOutMe ?? false,
      fileExtention: params.docType ?? "''",
      isIfsFileAccessible: true,
    };
    const client = getApiClient(accessToken);
    const filterString = encodeURIComponent(JSON.stringify(filters));
    const res = await client.get(
      `api/Document/get-search-records-count?filters=${filterString}`,
    );
    return { data: res.data, status: res.status };
  } catch (err) {
    logger.error("getSearchRecordsCount failed", err, "API");
    throw err;
  }
}

/**
 * Build Open-IFS URL(s) for a document.
 * @param {object} params
 * @param {object} params.items
 * @param {string} params.docType
 * @param {string} params.linkPlatform
 * @param {boolean} params.aurenaShowDetails
 * @param {string} accessToken
 * @returns {Promise<{ data: any, status: number }>}
 */
function mapDocumentUrlArrayError(data) {
  let errorMessage = "";
  for (const url of data ?? []) {
    if (url !== "Error" && typeof url === "string" && url.includes("Error")) {
      const errMsg = url.substring(12);
      errorMessage =
        errorMessage === "" ? errMsg : `${errorMessage}\n\n${errMsg}`;
    }
  }
  return {
    type: ERROR_TYPE,
    message: SOME_DOCUMENT_ERROR,
    errorMessage,
    error: null,
    __mappedDocumentUrlError: true,
  };
}

export function mapDocumentUrlError(errorOrResponse) {
  if (errorOrResponse?.__mappedDocumentUrlError) {
    return errorOrResponse;
  }
  const status =
    errorOrResponse?.status ?? errorOrResponse?.response?.status ?? 0;
  const data = errorOrResponse?.data ?? errorOrResponse?.response?.data;

  if (status === 201 || status === 202) {
    return {
      type: ERROR_TYPE,
      message: status === 201 ? GRAPH_SERVICE_ERROR : IFS_SERVER_ERROR,
      errorMessage: data
        ? `${data}`
        : `${ERROR_DETAILS} ${SERVER_RESPONSE_ERROR}`,
      error: errorOrResponse?.toJSON?.() ?? null,
      __mappedDocumentUrlError: true,
    };
  }

  if (status > 0 && status !== 200) {
    return {
      type: ERROR_TYPE,
      message: NO_CONTENT_FOUND,
      errorMessage: data
        ? `${data}`
        : `${ERROR_DETAILS} ${SERVER_RESPONSE_ERROR}`,
      error: errorOrResponse?.toJSON?.() ?? null,
      __mappedDocumentUrlError: true,
    };
  }

  const errJson = errorOrResponse?.toJSON?.() ?? {};
  return {
    type: ERROR_TYPE,
    message: FAILED_GENERATE_DIRECT_LINK,
    errorMessage: errorOrResponse?.response
      ? `${errJson.message ?? ""}`
      : errorOrResponse?.request
        ? `${ERROR_DETAILS} ${SERVER_RESPONSE_ERROR}`
        : errorOrResponse != null
          ? `${ERROR_DETAILS} ${errJson.message ?? ""}`
          : `${ERROR_DETAILS} ${UNKOWN_ERROR}`,
    error: errJson,
    __mappedDocumentUrlError: true,
  };
}

export async function getDocumentUrl(params) {
  logger.info("getDocumentUrl", null, "API");
  try {
    const client = getIfsApiClient();
    const webUser = new AuthTeams().getEmail();
    const { items, docType, linkPlatform, aurenaShowDetails } = params;
    const documentInfoList = [
      {
        DocClass: items.DocClass,
        DocNo: items.DocNo,
        DocSheet: items.DocSheet,
        DocRev: items.DocRev,
        ApplicationType: linkPlatform,
        DocType: docType,
        FileNo: 1,
        AurenaShowDetails: aurenaShowDetails,
      },
    ];
    const res = await client.post(
      `api/Document/get-document-url?webUser=${encodeURIComponent(webUser)}`,
      documentInfoList,
    );
    if (
      res.status === 200 &&
      res.data != null &&
      res.data.length > 1 &&
      res.data[0] === "Error"
    ) {
      throw mapDocumentUrlArrayError(res.data);
    }
    if (res.status !== 200) {
      throw mapDocumentUrlError(res);
    }
    return { data: res.data, status: res.status };
  } catch (err) {
    logger.error("getDocumentUrl failed", err, "API");
    throw mapDocumentUrlError(err);
  }
}

/**
 * Get IFS Cloud URL for a OneDrive/working document (api/get-ifs-document-url).
 * @param {{ item: { id?: string, documentId?: string, ItemsId?: string, DriveId?: string, DocClass?: string, DocNo?: string, DocSheet?: string, DocRev?: string, name?: string }, docType: string, linkPlatform: string, aurenaShowDetails: boolean }} params
 * @param {string} accessToken
 * @returns {Promise<{ data: string[] | any, status: number }>}
 */
export async function getIfsDocumentUrl(params, accessToken) {
  logger.info("getIfsDocumentUrl", null, "API");
  try {
    const client = getApiClient(accessToken);
    const webUser = new AuthTeams().getEmail();
    const { item, docType, linkPlatform, aurenaShowDetails } = params;
    const docId = item?.id ?? item?.documentId ?? "";
    const documentInfoList = [
      {
        OneDriveDocId: docId,
        OneDriveItemId: item?.ItemsId ?? item?.itemsId ?? "",
        OneDriveDriveId: item?.DriveId ?? item?.driveId ?? "",
        DocClass: item?.DocClass ?? item?.docClass ?? "",
        DocNo: item?.DocNo ?? item?.docNo ?? "",
        DocSheet: item?.DocSheet ?? item?.docSheet ?? "",
        DocRev: item?.DocRev ?? item?.docRev ?? "",
        Title: item?.name ?? item?.title ?? "",
        ApplicationType: linkPlatform,
        DocType: docType,
        FileNo: 1,
        AurenaShowDetails: aurenaShowDetails,
      },
    ];
    const res = await client.post(
      `api/Document/get-ifs-document-url?webUser=${encodeURIComponent(webUser)}`,
      documentInfoList,
    );
    if (res.status !== 200 && res.status !== 204) {
      throw mapDocumentUrlError(res);
    }
    const data =
      res.status === 200 && res.data != null && res.data.length > 0
        ? res.data
        : [];
    return { data: Array.isArray(data) ? data : [data], status: res.status };
  } catch (err) {
    logger.error("getIfsDocumentUrl failed", err, "API");
    throw mapDocumentUrlError(err);
  }
}

/**
 * Check-out / undo check-out documents.
 * @param {Array<{ webUser: string, docClass: string, docNo: string, docSheet: string, docRev: string, docType: string, fileNo: string, fileExtension: string, title: string, operationStatus: string, state?: string, fileState?: string, owner?: string, lastActionDate?: string, isDownloaded?: boolean }>} checkoutDocumentList
 * @returns {Promise<{ data: any, status: number }>}
 */
export async function checkoutDocuments(checkoutDocumentList) {
  logger.info(
    "checkoutDocuments",
    { count: checkoutDocumentList?.length },
    "API",
  );
  try {
    const client = getIfsApiClient();
    const res = await client.post(
      "/api/DocumentManagement/check-out",
      checkoutDocumentList,
    );
    return { data: res.data, status: res.status };
  } catch (err) {
    logger.error("checkoutDocuments failed", err, "API");
    throw err;
  }
}

/**
 * Multi check-out documents. Released and already checked-out items are skipped server-side.
 * @param {Array<object>} checkoutDocumentList
 * @returns {Promise<{ data: any, status: number }>}
 */
export async function checkoutDocumentsBulk(checkoutDocumentList) {
  logger.info(
    "checkoutDocumentsBulk",
    { count: checkoutDocumentList?.length },
    "API",
  );
  try {
    const client = getIfsApiClient();
    const timeout = getBulkCheckoutTimeoutMs(
      Array.isArray(checkoutDocumentList) ? checkoutDocumentList.length : 0,
    );
    const res = await client.post(
      ENDPOINTS.CHECKOUT.DOCUMENTS_BULK(),
      checkoutDocumentList,
      { timeout },
    );
    return { data: res.data, status: res.status };
  } catch (err) {
    logger.error("checkoutDocumentsBulk failed", err, "API");
    throw err;
  }
}

/**
 * Multi undo check-out documents. Released and already checked-in items are skipped server-side.
 * @param {Array<object>} checkoutDocumentList
 * @returns {Promise<{ data: any, status: number }>}
 */
export async function undoCheckoutDocumentsBulk(checkoutDocumentList) {
  logger.info(
    "undoCheckoutDocumentsBulk",
    { count: checkoutDocumentList?.length },
    "API",
  );
  try {
    const client = getIfsApiClient();
    const timeout = getBulkCheckoutTimeoutMs(
      Array.isArray(checkoutDocumentList) ? checkoutDocumentList.length : 0,
    );
    const res = await client.post(
      ENDPOINTS.CHECKOUT.UNDO_DOCUMENTS_BULK(),
      checkoutDocumentList,
      { timeout },
    );
    return { data: res.data, status: res.status };
  } catch (err) {
    logger.error("undoCheckoutDocumentsBulk failed", err, "API");
    throw err;
  }
}

/**
 * Get document revision/details info for MoreDetails modal.
 * @param {object} file
 * @param {string} accessToken
 * @param {{ latestRevision?: boolean }} [options] - When false, fetch the file's own revision (not IFS latest).
 * @returns {Promise<{ data: any, status: number }>}
 */
export async function getDocumentDetails(file, accessToken, options = {}) {
  logger.info("getDocumentDetails", null, "API");
  try {
    const client = getApiClient(accessToken);
    const ids = getWorkingDocumentDisplayDocIdentifiers(file);
    const docClass = ids.docClass || file?.DocClass || file?.class || "";
    const docNo = ids.docNo || file?.DocNo || file?.docNo || "";
    const docSheet = ids.docSheet || file?.DocSheet || file?.sheetNo || "";
    const docRev = ids.docRev || file?.DocRev || file?.docRev || "";
    const docId = file?.id || "";
    const driveId = file?.DriveId || "";

    const q = new URLSearchParams({
      docClass,
      docNo,
      docSheet,
      docRev,
      docId,
      driveId,
    });
    if (options.latestRevision === false) {
      q.set("latestRevision", "false");
    }

    const res = await client.get(
      `api/Document/get-document-revision-info?${q.toString()}`,
    );
    return { data: res.data, status: res.status };
  } catch (err) {
    logger.error("getDocumentDetails failed", err, "API");
    throw err;
  }
}

/**
 * Lightweight view-copy lookup for a working document (api/Document/get-view-copy-info).
 * Resolves keys from OneDrive when the file has none, then checks IFS for a VIEW file.
 * @param {object} file - Working document (id, DriveId and optional IFS keys)
 * @param {string} accessToken
 * @returns {Promise<{ hasViewCopy: boolean, fileNo?: number, fileExtension?: string, docClass?: string, docNo?: string, docSheet?: string, docRev?: string }>}
 */
export async function getViewCopyInfo(file, accessToken) {
  logger.info("getViewCopyInfo", null, "API");
  try {
    const client = getApiClient(accessToken);
    const ids = getWorkingDocumentDisplayDocIdentifiers(file);
    const q = new URLSearchParams({
      docClass: ids.docClass || file?.DocClass || "",
      docNo: ids.docNo || file?.DocNo || "",
      docSheet: ids.docSheet || file?.DocSheet || "",
      docRev: ids.docRev || file?.DocRev || "",
      docId: file?.id || "",
      driveId: file?.DriveId || "",
    });
    const res = await client.get(
      `api/Document/get-view-copy-info?${q.toString()}`,
    );
    return res.status === 200 && res.data
      ? res.data
      : { hasViewCopy: false };
  } catch (err) {
    logger.error("getViewCopyInfo failed", err, "API");
    throw err;
  }
}

/**
 * Get document new-revision info (includes the document's object connections).
 * Backend route: api/DocumentManagement/get-newrevisioninfo
 * @param {object} params
 * @param {string} params.docClass
 * @param {string} params.docNo
 * @param {string} params.docSheet
 * @param {string} params.docRev
 * @param {boolean} [params.requestDocRevInfo=false]
 * @returns {Promise<{ data: any, status: number }>}
 */
export async function getDocumentNewRevisionInfo({
  docClass,
  docNo,
  docSheet,
  docRev,
  requestDocRevInfo = false,
}) {
  logger.info("getDocumentNewRevisionInfo", { docClass, docNo, docRev }, "API");
  try {
    const client = getIfsApiClient();
    const q = new URLSearchParams({
      webUser: new AuthTeams().getEmail(),
      docClass,
      docNo,
      docSheet,
      docRev,
      requestDocRevInfo,
    });
    const res = await client.get(
      `api/DocumentManagement/get-newrevisioninfo?${q.toString()}`,
    );
    return { data: res.data, status: res.status };
  } catch (err) {
    logger.error("getDocumentNewRevisionInfo failed", err, "API");
    throw err;
  }
}

/**
 * Get working documents list (Teams/OneDrive).
 * @param {string} accessToken
 * @param {object} params
 * @param {string} params.teamId
 * @param {string} params.channelId
 * @param {string} params.tenantId
 * @param {string} params.fileRoot
 * @param {number} [params.displayCount=20]
 * @returns {Promise<{ data: any[], status: number }>} status 200 = data array, 204 = no content
 */
export async function getWorkingDocuments(accessToken, params) {
  logger.info(
    "getWorkingDocuments",
    { teamId: params?.teamId, channelId: params?.channelId },
    "API",
  );
  try {
    const config = new CustomerConfig();
    const client = getApiClient(accessToken);
    // const recFrom = 0;
    // const recSize = config.getDataLimit(config.get_OneDriveDataString());
    const recFrom = params?.recFrom ?? 0;
    const recSize = params?.recSize ?? 20;
    const searchText =
      typeof params?.searchText === "string" ? params.searchText : "";
    const fileType =
      typeof params?.fileType === "string" && params.fileType.length > 0
        ? params.fileType
        : "''";
    const filters = {
      userId: new AuthTeams().getUserId(),
      tenantId: params.tenantId,
      fileType,
      searchText,
      recFrom,
      recSize,
      teamId: params.teamId,
      channelId: params.channelId,
      clientId: new AuthTeams().getClientId(),
      oneDriveRoot: params.fileRoot,
      isOnedrive: false,
      // displayCount: params.displayCount ?? 20,
      displayCount: params.displayCount ?? recSize,
    };
    const filterString = JSON.stringify(filters);
    const res = await client.get(
      "api/OneDrive/get-working-documents?filter=" + filterString,
      {
        headers: {
          "E-Tag": config.getOneDriveETag() || "",
        },
      },
    );
    const data = res?.data?.value != null ? res.data.value : [];
    return { data, status: res.status };
  } catch (err) {
    logger.error("getWorkingDocuments failed", err, "API");
    throw err;
  }
}

/**
 * Get OneDrive/Teams document list.
 * @param {string} accessToken
 * @param {object} params
 * @param {string} params.tenantId
 * @param {string} params.teamId
 * @param {string} params.channelId
 * @param {string} params.fileType
 * @param {string} params.searchText
 * @param {number} params.recFrom
 * @param {number} params.recSize
 * @param {string} params.oneDriveRoot
 * @param {boolean} params.isOnedrive
 * @param {string} [params.eTag]
 * @returns {Promise<{ data: any, status: number }>}
 */
/**
 * Get Azure AD object ID (oid) from JWT access token. Used as userId fallback when localStorage userId is not set.
 */
function getUserIdFromToken(accessToken) {
  if (!accessToken || typeof accessToken !== "string") return null;
  try {
    const parts = accessToken.split(".");
    if (parts.length !== 3) return null;
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")),
    );
    return payload?.oid ?? payload?.sub ?? null;
  } catch {
    return null;
  }
}

/**
 * Build query string for get-onedrive-docs in same format as QA (no encoding).
 * QA URL: ...?userId=...&tenantId=...&fileType=&searchText=&recFrom=0&recSize=20&teamId=...&channelId=19:...@thread.tacv2&clientId=...&oneDriveRoot=ChannelDoc&isOnedrive=true
 */
function buildOneDriveDocsQuery(params, userId, clientId, pageLimit) {
  const recSize = params.recSize ?? pageLimit;
  const parts = [
    `userId=${userId ?? ""}`,
    `tenantId=${params.tenantId ?? ""}`,
    `fileType=${params.fileType ?? ""}`,
    `searchText=${params.searchText ?? ""}`,
    `recFrom=${params.recFrom ?? 0}`,
    `recSize=${recSize}`,
    `teamId=${params.teamId ?? ""}`,
    `channelId=${params.channelId ?? ""}`,
    `clientId=${clientId ?? ""}`,
    `oneDriveRoot=${params.oneDriveRoot ?? ""}`,
    `isOnedrive=${params.isOnedrive === true}`,
    `folderId=${params.folderId ?? ""}`,
    `fromDate=${params.fromDate ?? ""}`,
    `toDate=${params.toDate ?? ""}`,
  ];
  return parts.join("&");
}

export async function getOneDriveDocs(accessToken, params) {
  logger.info(
    "getOneDriveDocs",
    { teamId: params?.teamId, channelId: params?.channelId },
    "API",
  );
  try {
    const client = getApiClient(accessToken);
    const config = new CustomerConfig();
    const auth = new AuthTeams();
    const userId =
      auth.getUserId() ||
      getUserIdFromToken(accessToken) ||
      params.userId ||
      "";
    if (!userId) {
      logger.error(
        "getOneDriveDocs missing userId",
        { hasToken: !!accessToken },
        "API",
      );
      throw new Error(
        "userId is required for get-onedrive-docs. Ensure Teams context is loaded (context.user.id) or token contains oid.",
      );
    }
    const clientId = auth.getClientId();
    const eTag = params.eTag ?? config.getOneDriveETag() ?? "";
    const query = buildOneDriveDocsQuery(
      params,
      userId,
      clientId,
      config.getPageLimit(),
    );
    const res = await client.get(ENDPOINTS.ONEDRIVE.DOCS() + "?" + query, {
      headers: { "E-Tag": eTag },
    });
    return { data: res.data, status: res.status };
  } catch (err) {
    logger.error("getOneDriveDocs failed", err, "API");
    throw err;
  }
}

/**
 * Get documents list for check-in (landing page). Uses same backend as getOneDriveDocs.
 * Replace with a dedicated check-in API when available.
 * @param {string} accessToken
 * @param {object} params - same shape as getOneDriveDocs
 * @returns {Promise<{ data: any, status: number }>}
 */
export async function getCheckinDocuments(accessToken, params) {
  return getOneDriveDocs(accessToken, params);
}

/**
 * Delete OneDrive document.
 * @param {string} accessToken
 * @param {object} params
 * @param {string} params.fileId
 * @param {string} params.teamId
 * @param {string} [params.membershipType]
 * @param {string} [params.oneDriveRoot] - e.g. "MainMenu" or "ChannelDoc"
 * @param {string} [params.eTag]
 * @returns {Promise<{ status: number, data?: any }>}
 */
/**
 * Delete multiple OneDrive documents in one request (`POST api/OneDrive/del-onedrive-docs-bulk`).
 * @param {string} accessToken
 * @param {object} params
 * @param {string} params.tenantId
 * @param {string} [params.teamId]
 * @param {string} [params.channelId]
 * @param {string} params.oneDriveRoot
 * @param {string} [params.membershipType]
 * @param {boolean} [params.bypassSharedLock]
 * @param {string[]} params.documentIds
 * @returns {Promise<{ status: number, data?: object }>}
 */
export async function deleteOneDriveDocsBulk(accessToken, params) {
  const userId = new AuthTeams().getUserId();
  logger.info(
    "deleteOneDriveDocsBulk",
    { count: params?.documentIds?.length ?? 0 },
    "API",
  );
  try {
    const client = getApiClient(accessToken);
    const config = new CustomerConfig();
    const eTag = config.getOneDriveETag() ?? "";
    const body = {
      userId,
      tenantId: params.tenantId ?? "",
      teamId: params.teamId ?? "",
      channelId: params.channelId ?? "",
      oneDriveRoot: params.oneDriveRoot ?? "",
      membershipType: params.membershipType ?? "",
      bypassSharedLock: params.bypassSharedLock !== false,
      documentIds: params.documentIds ?? [],
    };
    const res = await client.post("api/OneDrive/del-onedrive-docs-bulk", body, {
      headers: { "E-Tag": eTag },
    });
    return { data: res.data, status: res.status };
  } catch (err) {
    logger.error("deleteOneDriveDocsBulk failed", err, "API");
    throw err;
  }
}

export async function deleteOneDriveDoc(accessToken, params) {
  logger.info(
    "deleteOneDriveDoc",
    { fileId: params?.fileId, teamId: params?.teamId },
    "API",
  );
  try {
    const client = getApiClient(accessToken);
    const config = new CustomerConfig();
    const userId = new AuthTeams().getUserId();
    const eTag = params.eTag ?? config.getOneDriveETag() ?? "";
    const q = new URLSearchParams({
      userId,
      teamId: params.teamId ?? "",
      fileId: params.fileId ?? "",
    });
    if (params.membershipType != null && params.membershipType !== "") {
      q.set("membershipType", params.membershipType);
    }
    if (params.oneDriveRoot != null && params.oneDriveRoot !== "") {
      q.set("oneDriveRoot", params.oneDriveRoot);
    }
    if (params.channelId != null && params.channelId !== "") {
      q.set("channelId", params.channelId);
    }
      if (params.bypassSharedLock != null) {
          q.set("bypassSharedLock", String(params.bypassSharedLock));
      }
    const res = await client.get(
      "api/OneDrive/del-onedrive-docs?" + q.toString(),
      {
        headers: { "E-Tag": eTag },
      },
    );
    return { data: res.data, status: res.status };
  } catch (err) {
    logger.error("deleteOneDriveDoc failed", err, "API");
    throw err;
  }
}

/**
 * Get OneDrive document download URL.
 * @param {string} accessToken
 * @param {object} params
 * @param {string} params.teamId
 * @param {string} params.channelId
 * @param {string} params.tenantId
 * @param {string} params.documentId
 * @param {string} params.oneDriveRoot
 * @returns {Promise<{ data: any, status: number }>}
 */
export async function getOneDriveDownloadUrl(accessToken, params) {
  logger.info(
    "getOneDriveDownloadUrl",
    { documentId: params?.documentId },
    "API",
  );
  try {
    const client = getApiClient(accessToken);
    const userId = new AuthTeams().getUserId();
    const q = new URLSearchParams({
      userId,
      teamId: params.teamId ?? "",
      channelId: params.channelId ?? "",
      tenantId: params.tenantId ?? "",
      documentId: params.documentId ?? "",
      oneDriveRoot: params.oneDriveRoot ?? "",
    });
    const res = await client.get(
      "api/OneDrive/get-onedrive-downloadUrl?" + q.toString(),
    );
    return { data: res.data, status: res.status };
  } catch (err) {
    logger.error("getOneDriveDownloadUrl failed", err, "API");
    throw err;
  }
}

/**
 * Get OneDrive document sharing details (people, groups, links).
 * @param {string} accessToken
 * @param {object} params
 * @param {string} params.teamId
 * @param {string} params.channelId
 * @param {string} params.tenantId
 * @param {string} params.documentId
 * @param {string} params.oneDriveRoot
 * @returns {Promise<{ data: any, status: number }>}
 */
export async function getOneDriveSharingDetails(accessToken, params) {
  logger.info(
    "getOneDriveSharingDetails",
    { documentId: params?.documentId },
    "API",
  );
  try {
    const client = getApiClient(accessToken);
    const userId = new AuthTeams().getUserId();
    const q = new URLSearchParams({
      userId,
      teamId: params.teamId ?? "",
      channelId: params.channelId ?? "",
      tenantId: params.tenantId ?? "",
      documentId: params.documentId ?? "",
      oneDriveRoot: params.oneDriveRoot ?? "",
    });
    const res = await client.get(
      "api/OneDrive/get-onedrive-sharing-details?" + q.toString(),
    );
    return { data: res.data, status: res.status };
  } catch (err) {
    logger.error("getOneDriveSharingDetails failed", err, "API");
    throw err;
  }
}

/**
 * Get OneDrive document version history (Microsoft Graph driveItem versions).
 * Returns an array of versions with version id, last-modified date, size and modified-by name.
 * @param {string} accessToken
 * @param {object} params
 * @param {string} params.teamId
 * @param {string} params.channelId
 * @param {string} params.tenantId
 * @param {string} params.documentId
 * @param {string} params.oneDriveRoot
 * @param {string} [params.driveId] - When provided, the drive is used directly (working documents); otherwise resolved from team/channel/root.
 * @returns {Promise<{ data: any, status: number }>}
 */
export async function getOneDriveVersionHistory(accessToken, params) {
  logger.info(
    "getOneDriveVersionHistory",
    { documentId: params?.documentId },
    "API",
  );
  try {
    const client = getApiClient(accessToken);
    const userId = new AuthTeams().getUserId();
    const q = new URLSearchParams({
      userId,
      teamId: params.teamId ?? "",
      channelId: params.channelId ?? "",
      tenantId: params.tenantId ?? "",
      documentId: params.documentId ?? "",
      oneDriveRoot: params.oneDriveRoot ?? "",
    });
    if (params.driveId) {
      q.set("driveId", params.driveId);
    }
    const res = await client.get(
      ENDPOINTS.ONEDRIVE.VERSION_HISTORY() + "?" + q.toString(),
    );
    const raw = res.data?.value != null ? res.data.value : res.data;
    const data =
      res.status === 204 ? [] : Array.isArray(raw) ? raw : raw != null ? [raw] : [];
    return { data, status: res.status };
  } catch (err) {
    logger.error("getOneDriveVersionHistory failed", err, "API");
    throw err;
  }
}

/**
 * Stop sharing a OneDrive document (remove non-owner permissions/links).
 * @param {string} accessToken
 * @param {object} params
 * @param {string} params.teamId
 * @param {string} params.channelId
 * @param {string} params.tenantId
 * @param {string} params.documentId
 * @param {string} params.oneDriveRoot
 * @returns {Promise<{ data: any, status: number }>}
 */
export async function stopOneDriveSharing(accessToken, params) {
  logger.info(
    "stopOneDriveSharing",
    { documentId: params?.documentId },
    "API",
  );
  try {
    const client = getApiClient(accessToken);
    const userId = new AuthTeams().getUserId();
    const q = new URLSearchParams({
      userId,
      teamId: params.teamId ?? "",
      channelId: params.channelId ?? "",
      tenantId: params.tenantId ?? "",
      documentId: params.documentId ?? "",
      oneDriveRoot: params.oneDriveRoot ?? "",
    });
    const res = await client.post(
      "api/OneDrive/stop-onedrive-sharing?" + q.toString(),
    );
    return { data: res.data, status: res.status };
  } catch (err) {
    logger.error("stopOneDriveSharing failed", err, "API");
    throw err;
  }
}

/**
 * Delete a sharing link, or remove one person from a "Specific people" link
 * when recipientEmail / recipientId is given. Returns refreshed sharing details.
 * @param {string} accessToken
 * @param {object} params - teamId, channelId, tenantId, documentId, oneDriveRoot,
 *   permissionId, recipientEmail?, recipientId?
 * @returns {Promise<{ data: any, status: number }>}
 */
export async function removeOneDriveSharingLink(accessToken, params) {
  logger.info(
    "removeOneDriveSharingLink",
    { documentId: params?.documentId, permissionId: params?.permissionId },
    "API",
  );
  try {
    const client = getApiClient(accessToken);
    const userId = new AuthTeams().getUserId();
    const q = new URLSearchParams({
      userId,
      teamId: params.teamId ?? "",
      channelId: params.channelId ?? "",
      tenantId: params.tenantId ?? "",
      documentId: params.documentId ?? "",
      oneDriveRoot: params.oneDriveRoot ?? "",
      permissionId: params.permissionId ?? "",
      recipientEmail: params.recipientEmail ?? "",
      recipientId: params.recipientId ?? "",
    });
    const res = await client.post(
      "api/OneDrive/remove-onedrive-sharing-link?" + q.toString(),
    );
    return { data: res.data, status: res.status };
  } catch (err) {
    logger.error("removeOneDriveSharingLink failed", err, "API");
    throw err;
  }
}

/**
 * Change one person's access ("read" | "write") on a document.
 * Returns refreshed sharing details.
 * @param {string} accessToken
 * @param {object} params - teamId, channelId, tenantId, documentId, oneDriveRoot,
 *   recipientEmail?, recipientId?, role
 * @returns {Promise<{ data: any, status: number }>}
 */
export async function updateOneDriveSharingRole(accessToken, params) {
  logger.info(
    "updateOneDriveSharingRole",
    { documentId: params?.documentId, role: params?.role },
    "API",
  );
  try {
    const client = getApiClient(accessToken);
    const userId = new AuthTeams().getUserId();
    const q = new URLSearchParams({
      userId,
      teamId: params.teamId ?? "",
      channelId: params.channelId ?? "",
      tenantId: params.tenantId ?? "",
      documentId: params.documentId ?? "",
      oneDriveRoot: params.oneDriveRoot ?? "",
      recipientEmail: params.recipientEmail ?? "",
      recipientId: params.recipientId ?? "",
      role: params.role ?? "read",
    });
    const res = await client.post(
      "api/OneDrive/update-onedrive-sharing-role?" + q.toString(),
    );
    return { data: res.data, status: res.status };
  } catch (err) {
    logger.error("updateOneDriveSharingRole failed", err, "API");
    throw err;
  }
}

/**
 * Download many OneDrive documents as one ZIP (`POST api/OneDrive/post-onedrive-bulk-download-zip`).
 * @param {string} accessToken
 * @param {object} params
 * @param {string} params.tenantId
 * @param {string} [params.teamId]
 * @param {string} [params.channelId]
 * @param {string} params.oneDriveRoot
 * @param {string[]} params.documentIds
 * @returns {Promise<import('axios').AxiosResponse<Blob>>}
 */
export async function postOneDriveBulkDownloadZip(accessToken, params) {
  const userId = new AuthTeams().getUserId();
  logger.info(
    "postOneDriveBulkDownloadZip",
    { count: params?.documentIds?.length ?? 0 },
    "API",
  );
  try {
    const client = getApiClient(accessToken);
    const body = {
      userId,
      tenantId: params.tenantId ?? "",
      teamId: params.teamId ?? "",
      channelId: params.channelId ?? "",
      oneDriveRoot: params.oneDriveRoot ?? "",
      documentIds: params.documentIds ?? [],
    };
    const res = await client.post(
      "api/OneDrive/post-onedrive-bulk-download-zip",
      body,
      {
        responseType: "blob",
        timeout: 480000,
      },
    );
    return res;
  } catch (err) {
    logger.error("postOneDriveBulkDownloadZip failed", err, "API");
    throw err;
  }
}

/**
 * Check-in existing document (working doc from OneDrive back to IFS).
 * @param {string} accessToken
 * @param {object} body
 * @param {boolean} body.checkinViewCopy
 * @param {string} body.docClass
 * @param {string} body.docNo
 * @param {string} body.docRev
 * @param {string} body.title - URI-encoded title
 * @param {string} body.docFormat
 * @param {string} body.docSheet
 * @param {string} body.fileData - data URL (e.g. data:application/...;base64,...)
 * @param {string} body.fileName
 * @param {Array} body.objectConnections
 * @param {boolean} body.createViewCopy
 * @param {boolean} body.rollbackRevisionOnFail
 * @param {string} body.webUser
 * @param {string} body.status - release status or ""
 * @param {{ timeout?: number, fileSizeBytes?: number, viewCopyBase64Length?: number }} [options]
 * @returns {Promise<{ data: any, status: number }>}
 */
export async function checkInExistingDocument(accessToken, body, options = {}) {
  logger.info("checkInExistingDocument", { docNo: body?.docNo }, "API");
  try {
    const client = getApiClient(accessToken);
    const timeout =
      options.timeout ??
      getCheckinExistingTimeoutMs({
        fileSizeBytes: options.fileSizeBytes,
        viewCopyBase64Length: options.viewCopyBase64Length,
      });
    const res = await client.post(
      "api/DocumentManagement/check-in-existing",
      body,
      { timeout },
    );
    return { data: res.data, status: res.status };
  } catch (err) {
    logger.error("checkInExistingDocument failed", err, "API");
    throw err;
  }
}

/**
 * Check-in new revision. POST api/check-in/new-revision.
 * @param {object} body - { documentRevision: object, newRevision: string, newRevisionText: string }
 * @returns {Promise<{ data: any, status: number }>}
 */
export async function postCheckinNewRevision(body) {
  logger.info(
    "postCheckinNewRevision",
    { docNo: body?.documentRevision?.docNo },
    "API",
  );
  try {
    const client = getIfsApiClient();
    const res = await client.post(
      "api/DocumentManagement/check-in/new-revision",
      body,
    );
    return { data: res.data, status: res.status };
  } catch (err) {
    logger.error("postCheckinNewRevision failed", err, "API");
    throw err;
  }
}

/**
 * Check-in a document or view copy. POST api/DocumentManagement/check-in.
 * @param {string} accessToken
 * @param {object} body - DocInfoModel payload
 * @param {{ timeout?: number }} [options]
 * @returns {Promise<{ data: any, status: number }>}
 */
export async function postCheckinDocument(accessToken, body, options = {}) {
  logger.info(
    "postCheckinDocument",
    { docNo: body?.docNo, checkinViewCopy: body?.checkinViewCopy },
    "API",
  );
  try {
    const client = getApiClient(accessToken);
    const res = await client.post("api/DocumentManagement/check-in", body, {
      timeout: options.timeout ?? 60000,
    });
    return { data: res.data, status: res.status };
  } catch (err) {
    logger.error("postCheckinDocument failed", err, "API");
    throw err;
  }
}

/**
 * Approve and release a document revision after check-in (IFS GetSecurityCheckState + ApproveAndReleaseRevNoSecCheck).
 * @param {object} body - { docClass, docNo, docSheet, docRev }
 * @returns {Promise<{ data: any, status: number }>}
 */
export async function approveAndReleaseRevision(body) {
  logger.info(
    "approveAndReleaseRevision",
    { docNo: body?.docNo, docRev: body?.docRev },
    "API",
  );
  try {
    const client = getIfsApiClient();
    const res = await client.post(
      "api/DocumentManagement/approve-and-release-revision",
      body,
    );
    return { data: res.data, status: res.status };
  } catch (err) {
    logger.error("approveAndReleaseRevision failed", err, "API");
    throw err;
  }
}

/**
 * Upload IFS document(s) to OneDrive.
 * @param {string} accessToken
 * @param {object} params
 * @param {string} params.tenantId
 * @param {string} [params.teamId]
 * @param {string} [params.channelId]
 * @param {string} params.oneDriveRoot
 * @param {Array<{ class, docNo, sheetNo, docRev, docType, fileNo, title, fileExtension }>} params.items
 * @returns {Promise<{ data: any, status: number }>}
 */
/**
 * Build query string for upload-to-onedrive so channelId and webUser are not encoded
 * (backend/QA expects unencoded format: channelId=19:...@thread.tacv2, webUser=user@domain.com).
 * oneDriveRoot sends the string "undefined" when value is missing to match backend expectation.
 */
function buildUploadToOneDriveQuery(
  userId,
  tenantId,
  teamId,
  channelId,
  oneDriveRoot,
  webUser,
) {
  const root =
    oneDriveRoot != null && oneDriveRoot !== "" ? oneDriveRoot : "undefined";
  const parts = [
    `userId=${userId}`,
    `tenantId=${tenantId ?? ""}`,
    `teamId=${teamId ?? ""}`,
    `channelId=${channelId ?? ""}`,
    `oneDriveRoot=${root}`,
    `webUser=${webUser}`,
  ];
  return parts.join("&");
}

/**
 * OneDrive file-name restrictions for title fragment:
 * disallow: " * : < > ? / \ | # % and control chars; also trim trailing dots/spaces.
 * Keep other unicode characters so user-visible names remain readable.
 */
function sanitizeOneDriveTitle(title) {
  const raw = title == null ? "" : String(title);
  const cleaned = raw
    .replace(/["*:<>?/\\|#%]/g, " ")
    .replace(/[\u0000-\u001F]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/[. ]+$/g, "");
  return cleaned || "document";
}

/**
 * @param {string} accessToken
 * @param {object} params
 * @param {{ timeout?: number }} [options]
 */
export async function uploadToOneDrive(accessToken, params, options = {}) {
  logger.info(
    "uploadToOneDrive",
    { oneDriveRoot: params?.oneDriveRoot },
    "API",
  );
  try {
    const client = getApiClient(accessToken);
    const userId = new AuthTeams().getUserId();
    const webUser = new AuthTeams().getEmail();
    const {
      tenantId,
      teamId = "",
      channelId = "",
      oneDriveRoot,
      items,
    } = params;
    const query = buildUploadToOneDriveQuery(
      userId,
      tenantId ?? "",
      teamId ?? "",
      channelId ?? "",
      oneDriveRoot,
      webUser,
    );
    const body = (items ?? []).map((item) => {
      const base = {
        docClass: item.class,
        docNo: item.docNo,
        docSheet: item.sheetNo,
        docRev: item.docRev,
        docType: item.docType,
        fileNo: item.fileNo,
        Title: sanitizeOneDriveTitle(item.title),
        fileExtension: item.fileExtension,
      };
      if (item.state != null) base.state = item.state;
      if (item.fileState != null) base.fileState = item.fileState;
      if (item.owner != null) base.owner = item.owner;
      if (item.lastActionDate != null)
        base.lastActionDate = item.lastActionDate;
      if (item.isDownloaded != null) base.isDownloaded = item.isDownloaded;
      return base;
    });
    const res = await client.post(
      `api/Document/upload-to-onedrive?${query.toString()}`,
      body,
      {
        timeout: options.timeout ?? UPLOAD_TO_ONEDRIVE_TIMEOUT_MS,
      },
    );
    return { data: res.data, status: res.status };
  } catch (err) {
    logger.error("uploadToOneDrive failed", err, "API");
    throw err;
  }
}

/**
 * Update metadata fields on an existing OneDrive document (e.g. after check-in).
 * @param {string} accessToken
 * @param {object} params
 * @param {string} params.userId
 * @param {string} params.tenantId
 * @param {string} [params.teamId]
 * @param {string} [params.channelId]
 * @param {string} params.oneDriveRoot
 * @param {string} params.documentId - OneDrive item ID
 * @param {string} params.webUser
 * @param {object} params.metadata - { docClass, docNo, docRev, docSheet, formatSize?, state?, fileState?, owner?, lastActionDate?, isDownloaded? }
 * @returns {Promise<{ data: any, status: number }>}
 */
export async function updateOneDriveMetadata(accessToken, params) {
  logger.info("updateOneDriveMetadata", { documentId: params?.documentId }, "API");
  try {
    const client = getApiClient(accessToken);
    const q = new URLSearchParams({
      userId: params.userId ?? "",
      teamId: params.teamId ?? "",
      channelId: params.channelId ?? "",
      tenantId: params.tenantId ?? "",
      oneDriveRoot: params.oneDriveRoot ?? "",
      documentId: params.documentId ?? "",
      webUser: params.webUser ?? "",
    });
    const res = await client.post(
      `api/OneDrive/update-onedrive-metadata?${q.toString()}`,
      params.metadata ?? {},
    );
    return { data: res.data, status: res.status };
  } catch (err) {
    logger.error("updateOneDriveMetadata failed", err, "API");
    throw err;
  }
}

/**
 * Upload IFS document to OneDrive for preview (Preview in Teams).
 * @returns {Promise<{ documentId: string, status: number }>} documentId on 200
 */
export async function uploadToOneDriveForPreview(accessToken, params) {
  const { data, status } = await uploadToOneDrive(accessToken, {
    ...params,
    oneDriveRoot: params.oneDriveRoot ?? "",
  });
  if (status === 200 && data?.[0]?.value?.id != null) {
    return { documentId: data[0].value.id, status };
  }
  throw new Error(data?.message ?? `Upload status ${status}`);
}

/**
 * Get OneDrive document preview URL.
 * @param {string} accessToken
 * @param {object} params
 * @param {string} params.teamId
 * @param {string} params.channelId
 * @param {string} params.tenantId
 * @param {string} params.documentId
 * @param {string} params.oneDriveRoot
 * @returns {Promise<{ data: any, status: number }>}
 */
export async function getOneDrivePreviewUrl(accessToken, params) {
  logger.info(
    "getOneDrivePreviewUrl",
    { documentId: params?.documentId },
    "API",
  );
  try {
    const client = getApiClient(accessToken);
    const userId = new AuthTeams().getUserId();
    const q = new URLSearchParams({
      userId,
      teamId: params.teamId ?? "",
      channelId: params.channelId ?? "",
      tenantId: params.tenantId ?? "",
      documentId: params.documentId ?? "",
      oneDriveRoot: params.oneDriveRoot ?? "",
    });
    const res = await client.get("api/OneDrive/get-previewUrl?" + q.toString());
    return { data: res.data, status: res.status };
  } catch (err) {
    logger.error("getOneDrivePreviewUrl failed", err, "API");
    throw err;
  }
}

const ONEDRIVE_ROOT_IFS_PREVIEW = "IfsDocPreview"; 

/**
 * Get preview-in-Teams URL. Handles both scenarios in the API layer:
 * - IFS: upload to OneDrive for preview, then get preview URL (same endpoint).
 * - OneDrive: get preview URL only.
 * @param {string} accessToken
 * @param {object} params
 * @param {object} [params.item] - IFS item: { class, docNo, sheetNo, docRev, docType, fileNo, title, fileExtension }
 * @param {string} [params.documentId] - OneDrive document id (required when no item)
 * @param {string} [params.fileRoot] - OneDrive only: MainMenu | ChannelDoc
 * @param {string} params.teamId
 * @param {string} params.channelId
 * @param {string} params.tenantId
 * @returns {Promise<{ data: any, status: number }>} same shape as getOneDrivePreviewUrl
 */
export async function getPreviewInTeamsUrl(accessToken, params) {
  const { teamId, channelId, tenantId } = params;
  let documentId = params.documentId;
  let oneDriveRoot = params.fileRoot ?? "";

  if (
    params.item != null &&
    params.item.class != null &&
    params.item.docNo != null
  ) {
    const item = params.item;
    const safeTitle = sanitizeFileName(
      ((item.title ?? "document").replace(/[<>:"\/\\|?*]/g, "") || "document") + ".tmp",
    ).replace(/\.tmp$/, "");
    const items = [
      {
        class: item.class,
        docNo: item.docNo,
        sheetNo: item.sheetNo,
        docRev: item.docRev,
        docType: item.docType,
        fileNo: item.fileNo,
        title: safeTitle,
        fileExtension: item.fileExtension,
      },
    ];
    logger.info(
      "getPreviewInTeamsUrl IFS upload",
      { docNo: item.docNo },
      "API",
    );
    const uploadResult = await uploadToOneDriveForPreview(accessToken, {
      tenantId,
      teamId: teamId ?? "",
      channelId: channelId ?? "",
      oneDriveRoot: ONEDRIVE_ROOT_IFS_PREVIEW,
      items,
    });
    documentId = uploadResult.documentId;
    if (!documentId) {
      return {
        data: { value: null, errorMessage: "Preview not found" },
        status: 404,
      };
    }
    oneDriveRoot = ONEDRIVE_ROOT_IFS_PREVIEW;
  }

  return getOneDrivePreviewUrl(accessToken, {
    teamId: teamId ?? "",
    channelId: channelId ?? "",
    tenantId,
    documentId,
    oneDriveRoot,
  });
}
